/**
 * Settings → Communication → Outbound webhooks (G17).
 *
 * Vite SPA port of:
 *   frontend/src/app/(dashboard)/dashboard/settings/webhooks/page.tsx
 *   frontend/src/components/settings/webhook-form.tsx
 *   frontend/src/hooks/use-webhooks.ts
 *
 * HR-admins can configure outbound Slack / MS Teams webhook subscriptions,
 * and (wave 2) GENERIC signed webhooks: any HTTPS endpoint receives the
 * JSON event with an HMAC-SHA256 signature. A generic webhook subscribes
 * with event_types (all events, or a picked list — POSH notices only when
 * picked), shows its signing secret ONCE (after create / rotate), and logs
 * every attempt incl. retries. The backend returns URLs masked so the plain
 * URL never appears after creation.
 *
 * Features:
 *   - List: label, provider badge, category, masked URL, active status,
 *     last-delivery status.
 *   - Add: CreateWebhookDialog (provider, URL, label, category, is_active).
 *   - Edit: label / category / is_active only (URL is immutable post-create).
 *   - Disable: toggle is_active via PATCH.
 *   - Delete: with an inline confirm AlertDialog.
 *   - Test fire: POST /{id}/test, shows inline result toast.
 *   - Delivery log: expandable panel per webhook (pages of 50, newest
 *     first, "Load older deliveries"); a generic delivery that failed for
 *     good and is still kept gets a "Redeliver" button (migration 132).
 *   - Health: "N failed in a row" badge; a webhook WorkRight turned off
 *     after repeated failures shows a banner with "Turn back on".
 *   - Delivery history: per-tenant log retention (7–365 days).
 *
 * Migration note (Wave F, 2026-06-05): direct port following Vite SPA
 * conventions — TanStack Router createFileRoute, useHrRoles() for the
 * HR-admin gate, SettingsShell + SettingsPageHeader wrapper.
 */

import { useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import {
  AlertTriangle,
  CheckCircle2,
  ChevronDown,
  ChevronUp,
  FlaskConical,
  History,
  Loader2,
  Pencil,
  Plus,
  Power,
  RotateCw,
  Send,
  Trash2,
  Webhook,
} from "lucide-react";
import { toast } from "sonner";

import { toApiError } from "@/lib/api";
import { formatDateTime } from "@/lib/format";
import {
  listWebhooks,
  listWebhookDeliveries,
  createWebhook,
  updateWebhook,
  deleteWebhook,
  testWebhook,
  redeliverWebhookDelivery,
  enableWebhook,
  getWebhookSettings,
  updateWebhookSettings,
  type OutboundWebhook,
  type CreateWebhookRequest,
  type UpdateWebhookRequest,
  type WebhookEventType,
} from "@/lib/api/webhooks";
import {
  isCatalogCategory,
  redeliverRowIds,
  validRetentionDays,
} from "@/lib/webhook-format";
import { useHrRoles } from "@/components/hr/use-hr-roles";
import {
  DeadLetterPanel,
  EventTypePicker,
  MAX_EVENT_TYPES,
  RotateSecretButton,
  SigningSecretDialog,
  genericSubscriptionLabel,
  useWebhookEventTypes,
} from "@/components/settings/generic-webhook-parts";
import { Label } from "@/components/ui/label";
import { StatusBadge } from "@/components/shared/status-badge";
import { SettingsPageHeader } from "@/components/shared/settings-page-header";
import { SettingsShell } from "@/components/shared/settings-shell";
import { SETTINGS_GROUPS } from "@/lib/settings-nav";
import { webhookErrorText } from "@/lib/webhook-error-text";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Alert,
  AlertDescription,
  AlertTitle,
} from "@/components/ui/alert";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

// ---------------------------------------------------------------------------
// Route
// ---------------------------------------------------------------------------

export const Route = createFileRoute(
  "/_authgated/app/$tenantSlug/settings/webhooks",
)({
  component: SettingsWebhooksRoute,
});

// ---------------------------------------------------------------------------
// Query keys
// ---------------------------------------------------------------------------

const webhookKeys = {
  all: ["webhooks"] as const,
  list: () => [...webhookKeys.all, "list"] as const,
  deliveries: (id: number) =>
    [...webhookKeys.all, "deliveries", id] as const,
  deadLetters: (id: number) =>
    [...webhookKeys.all, "dead-letters", id] as const,
  settings: () => [...webhookKeys.all, "settings"] as const,
};

function invalidateList(qc: ReturnType<typeof useQueryClient>) {
  void qc.invalidateQueries({ queryKey: webhookKeys.list() });
}

// ---------------------------------------------------------------------------
// Hooks
// ---------------------------------------------------------------------------

function useWebhooks() {
  return useQuery({
    queryKey: webhookKeys.list(),
    queryFn: ({ signal }) => listWebhooks(signal),
    staleTime: 30 * 1000,
  });
}

// The delivery log pages newest-first with a keyset cursor (next_cursor);
// "Load older deliveries" fetches the next page.
function useWebhookDeliveriesQuery(id: number | null) {
  return useInfiniteQuery({
    queryKey: webhookKeys.deliveries(id ?? 0),
    queryFn: ({ signal, pageParam }) =>
      listWebhookDeliveries(id!, signal, pageParam),
    initialPageParam: null as string | null,
    getNextPageParam: (last) => last.next_cursor ?? undefined,
    enabled: id != null,
    staleTime: 10 * 1000,
  });
}

function useRedeliver(webhookId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (rowId: number) => redeliverWebhookDelivery(webhookId, rowId),
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: webhookKeys.deliveries(webhookId) });
      void qc.invalidateQueries({ queryKey: webhookKeys.deadLetters(webhookId) });
      invalidateList(qc);
    },
  });
}

function useEnableWebhook() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => enableWebhook(id),
    onSuccess: () => invalidateList(qc),
  });
}

function useCreateWebhook() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateWebhookRequest) => createWebhook(body),
    onSuccess: () => invalidateList(qc),
  });
}

function useUpdateWebhook() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { id: number; body: UpdateWebhookRequest }) =>
      updateWebhook(vars.id, vars.body),
    onSuccess: () => invalidateList(qc),
  });
}

function useDeleteWebhook() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => deleteWebhook(id),
    onSuccess: () => invalidateList(qc),
  });
}

function useTestWebhook() {
  return useMutation({
    mutationFn: (id: number) => testWebhook(id),
  });
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

// Slack / Teams subscribe with ONE category: "*" or a type key. The options
// come from the same server catalog as the generic EventTypePicker
// (GET /outbound-webhooks/event-types), so every option is a notification
// type or event the backend actually emits — the old hard-coded list
// offered keys nothing sent (a saved subscription silently never fired).
const ALL_CATEGORY = { value: "*", label: "All Notifications" } as const;

function CategorySelect({
  value,
  onChange,
  testId,
}: {
  readonly value: string;
  readonly onChange: (v: string) => void;
  readonly testId?: string;
}) {
  const q = useWebhookEventTypes(true);
  const catalog = q.data?.items ?? [];
  const groups = new Map<string, WebhookEventType[]>();
  for (const e of catalog) {
    groups.set(e.group, [...(groups.get(e.group) ?? []), e]);
  }
  const selected = catalog.find((e) => e.type === value);
  // A saved category outside the catalog stays selectable so the row
  // renders, flagged so HR can check it. The catalog now lists every type
  // WorkRight sends (incl. invoice.posted / bill.cancelled and the "All …
  // events" families), so an unlisted value is most likely a typo or a
  // retired key — but it is not provably "never sent", so it is not
  // labelled that way any more.
  const legacy =
    value !== "" && q.isSuccess && !isCatalogCategory(value, catalog);

  return (
    <div className="space-y-1.5">
      <Select value={value} onValueChange={onChange}>
        <FormControl>
          <SelectTrigger data-testid={testId}>
            <SelectValue placeholder="Select type" />
          </SelectTrigger>
        </FormControl>
        <SelectContent>
          <SelectItem value={ALL_CATEGORY.value}>{ALL_CATEGORY.label}</SelectItem>
          {(legacy || (!q.isSuccess && value !== "" && value !== ALL_CATEGORY.value)) && (
            <SelectItem value={value}>
              {legacy ? `${value} (not in the list — check the name)` : value}
            </SelectItem>
          )}
          {[...groups.entries()].map(([group, items]) => (
            <SelectGroup key={group}>
              <SelectLabel>{group}</SelectLabel>
              {items.map((e) => (
                <SelectItem key={e.type} value={e.type}>
                  {e.label}
                </SelectItem>
              ))}
            </SelectGroup>
          ))}
        </SelectContent>
      </Select>
      {q.isError && (
        <p className="text-xs text-destructive">
          Couldn&apos;t load notification types: {toApiError(q.error).message}
        </p>
      )}
      {legacy && (
        <p className="text-xs text-destructive">
          &quot;{value}&quot; is not one of the types WorkRight lists, so
          this webhook may receive nothing. Pick a type from the list.
        </p>
      )}
      {selected?.confidential && (
        <p className="text-xs text-muted-foreground">
          POSH notices carry no names or complaint details, but their timing
          is confidential. Send them only to a channel restricted to the ICC.
        </p>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function providerLabel(p: string) {
  if (p === "generic") return "Signed HTTPS";
  return p === "teams" ? "MS Teams" : "Slack";
}

function categoryLabel(w: OutboundWebhook) {
  if (w.provider === "generic") return genericSubscriptionLabel(w);
  if (w.category === "*") return "All Notifications";
  return w.category.replaceAll("_", " ");
}

function deliveryStatusTone(
  status: number | null | undefined,
): "success" | "danger" | "neutral" {
  if (status == null) return "neutral";
  if (status >= 200 && status < 300) return "success";
  return "danger";
}

// ---------------------------------------------------------------------------
// Form schemas
// ---------------------------------------------------------------------------

// Generic webhooks subscribe with event_types (all_events = []), Slack /
// Teams with category — one schema, provider-aware refinement.
const subscriptionRefine = (
  v: {
    provider: string;
    category: string;
    all_events: boolean;
    event_types: string[];
  },
  ctx: z.RefinementCtx,
) => {
  if (v.provider !== "generic" && !v.category) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ["category"],
      message: "Category is required",
    });
  }
  if (v.provider === "generic" && !v.all_events && v.event_types.length === 0) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ["event_types"],
      message: "Pick at least one event, or choose All events",
    });
  }
  if (v.event_types.length > MAX_EVENT_TYPES) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ["event_types"],
      message: `At most ${MAX_EVENT_TYPES} event types per webhook`,
    });
  }
};

const createSchema = z
  .object({
    provider: z.enum(["slack", "teams", "generic"]),
    url: z
      .string()
      .min(1, "Webhook URL is required")
      .regex(/^https:\/\/.+/, "Must be a valid HTTPS URL"),
    label: z
      .string()
      .min(1, "Label is required")
      .max(100, "Label must be 100 characters or fewer"),
    category: z.string(),
    all_events: z.boolean(),
    event_types: z.array(z.string()),
    is_active: z.boolean(),
  })
  .superRefine(subscriptionRefine);

const editSchema = z
  .object({
    provider: z.enum(["slack", "teams", "generic"]),
    label: z
      .string()
      .min(1, "Label is required")
      .max(100, "Label must be 100 characters or fewer"),
    category: z.string(),
    all_events: z.boolean(),
    event_types: z.array(z.string()),
    is_active: z.boolean(),
  })
  .superRefine(subscriptionRefine);

type CreateFormValues = z.infer<typeof createSchema>;
type EditFormValues = z.infer<typeof editSchema>;

// ---------------------------------------------------------------------------
// DeliveryLog — expandable per-webhook panel
// ---------------------------------------------------------------------------

function DeliveryLog({
  webhookId,
  canRedeliver,
}: {
  readonly webhookId: number;
  readonly canRedeliver: boolean;
}) {
  const q = useWebhookDeliveriesQuery(webhookId);
  const redeliver = useRedeliver(webhookId);
  const rows = q.data?.pages.flatMap((p) => p.items) ?? [];
  const redeliverable = redeliverRowIds(rows);

  async function handleRedeliver(rowId: number) {
    try {
      const res = await redeliver.mutateAsync(rowId);
      if (res.delivered) {
        toast.success(`Redelivered — the endpoint responded ${res.status_code}.`);
      } else {
        // The receiver's reply reduced to one plain line (an HTML error page
        // to its title) — the same cleaning the delivery log uses.
        const reply = webhookErrorText(res.error).replace(/[.\s]+$/, "");
        const detail = reply ? `: ${reply}` : "";
        toast.error(
          `Redelivery failed (${res.status_code || "no response"})${detail}.` +
            (res.parked ? " It is still kept — you can try again." : ""),
        );
      }
    } catch (err) {
      toast.error(toApiError(err).message);
    }
  }

  if (q.isLoading) {
    return (
      <div className="space-y-1 px-4 pb-4">
        {["d1", "d2", "d3"].map((k) => (
          <Skeleton key={k} className="h-8 w-full" />
        ))}
      </div>
    );
  }

  if (q.isError) {
    return (
      <p className="px-4 pb-4 text-sm text-destructive">
        {toApiError(q.error).message}
      </p>
    );
  }

  if (rows.length === 0) {
    return (
      <p className="px-4 pb-4 text-sm text-muted-foreground">
        No deliveries recorded yet.
      </p>
    );
  }

  return (
    <ul
      className="divide-y divide-border px-4 pb-4"
      data-testid={`webhook-deliveries-${webhookId}`}
    >
      {rows.map((d) => (
        <li
          key={d.id}
          className="flex flex-wrap items-start justify-between gap-2 py-2 text-xs"
        >
          <div className="min-w-0">
            <span className="font-medium">{d.notif_category}</span>
            <span className="ml-2 text-muted-foreground">
              {d.payload_summary}
            </span>
            {d.attempt != null && (
              <span className="ml-2 text-muted-foreground">
                · attempt {d.attempt}
                {d.duration_ms != null && ` · ${d.duration_ms} ms`}
              </span>
            )}
            {d.error && (
              <p className="mt-0.5 break-words text-destructive">
                {webhookErrorText(d.error)}
              </p>
            )}
            {d.next_retry_at && (
              <p className="mt-0.5 text-muted-foreground">
                Retry scheduled {formatDateTime(d.next_retry_at)}
              </p>
            )}
            {d.delivery_id && (
              <p
                className="mt-0.5 font-mono text-[11px] text-muted-foreground"
                title="X-WorkRight-Delivery — the same id on every retry"
              >
                {d.delivery_id}
              </p>
            )}
          </div>
          <div className="flex shrink-0 items-center gap-2">
            {canRedeliver && redeliverable.has(d.id) && (
              <Button
                type="button"
                size="sm"
                variant="outline"
                className="h-7 gap-1 px-2 text-xs"
                disabled={redeliver.isPending}
                onClick={() => void handleRedeliver(d.id)}
                title="Resend this delivery now — same delivery id, fresh signature"
                data-testid={`webhook-redeliver-${d.id}`}
              >
                {redeliver.isPending && redeliver.variables === d.id ? (
                  <Loader2 aria-hidden className="h-3 w-3 animate-spin" />
                ) : (
                  <RotateCw aria-hidden className="h-3 w-3" />
                )}
                Redeliver
              </Button>
            )}
            <StatusBadge
              tone={deliveryStatusTone(d.status_code)}
              label={d.status_code == null ? "—" : String(d.status_code)}
            />
            <span className="text-muted-foreground">
              {formatDateTime(d.sent_at)}
            </span>
          </div>
        </li>
      ))}
      {q.hasNextPage && (
        <li className="pt-2">
          <Button
            type="button"
            size="sm"
            variant="ghost"
            className="gap-1.5 text-xs"
            disabled={q.isFetchingNextPage}
            onClick={() => void q.fetchNextPage()}
            data-testid={`webhook-deliveries-more-${webhookId}`}
          >
            {q.isFetchingNextPage ? (
              <Loader2 aria-hidden className="h-3.5 w-3.5 animate-spin" />
            ) : (
              <History aria-hidden className="h-3.5 w-3.5" />
            )}
            Load Older Deliveries
          </Button>
        </li>
      )}
    </ul>
  );
}

// ---------------------------------------------------------------------------
// AutoDisabledBanner — the webhook was turned off after repeated failures
// ---------------------------------------------------------------------------

function AutoDisabledBanner({
  webhook,
  isHrAdmin,
}: {
  readonly webhook: OutboundWebhook;
  readonly isHrAdmin: boolean;
}) {
  const enableMut = useEnableWebhook();
  if (webhook.is_active || !webhook.auto_disabled_at) return null;

  async function handleEnable() {
    try {
      await enableMut.mutateAsync(webhook.id);
      toast.success(`Webhook "${webhook.label}" is back on.`);
    } catch (err) {
      toast.error(toApiError(err).message);
    }
  }

  return (
    <Alert variant="destructive" data-testid={`webhook-auto-disabled-${webhook.id}`}>
      <AlertTriangle aria-hidden className="h-4 w-4" />
      <AlertTitle>Turned off after repeated failures</AlertTitle>
      <AlertDescription className="space-y-2">
        <p>
          WorkRight stopped calling this endpoint on{" "}
          {formatDateTime(webhook.auto_disabled_at)} after{" "}
          {webhook.consecutive_failures ?? 0} deliveries in a row failed
          {webhook.last_status != null && ` (latest: HTTP ${webhook.last_status})`}.
          Fix the receiving end, then turn it back on. Deliveries that failed
          meanwhile are kept for redelivery.
        </p>
        {isHrAdmin && (
          <Button
            type="button"
            size="sm"
            variant="outline"
            className="gap-1.5"
            disabled={enableMut.isPending}
            onClick={() => void handleEnable()}
            data-testid={`webhook-reenable-${webhook.id}`}
          >
            {enableMut.isPending ? (
              <Loader2 aria-hidden className="h-3.5 w-3.5 animate-spin" />
            ) : (
              <Power aria-hidden className="h-3.5 w-3.5" />
            )}
            Turn Back On
          </Button>
        )}
      </AlertDescription>
    </Alert>
  );
}

// ---------------------------------------------------------------------------
// DeliveryRetentionCard — how long the delivery log is kept
// ---------------------------------------------------------------------------

function DeliveryRetentionCard({ isHrAdmin }: { readonly isHrAdmin: boolean }) {
  const qc = useQueryClient();
  const q = useQuery({
    queryKey: webhookKeys.settings(),
    queryFn: ({ signal }) => getWebhookSettings(signal),
    enabled: isHrAdmin,
    staleTime: 60 * 1000,
  });
  const [draft, setDraft] = useState<string | null>(null);
  const save = useMutation({
    mutationFn: (days: number) =>
      updateWebhookSettings({ delivery_log_retention_days: days }),
    onSuccess: (s) => {
      qc.setQueryData(webhookKeys.settings(), s);
      setDraft(null);
      toast.success(`Delivery history is now kept for ${s.delivery_log_retention_days} days.`);
    },
    onError: (err) => toast.error(toApiError(err).message),
  });

  if (!isHrAdmin) return null;
  if (q.isLoading) return <Skeleton className="h-28 w-full" />;
  if (q.isError) {
    return (
      <p className="text-sm text-destructive">{toApiError(q.error).message}</p>
    );
  }
  const s = q.data;
  if (!s) return null;
  const value = draft ?? String(s.delivery_log_retention_days);
  const parsed = validRetentionDays(value, s.min_retention_days, s.max_retention_days);
  const dirty = parsed !== null && parsed !== s.delivery_log_retention_days;

  return (
    <Card data-testid="webhook-retention-card">
      <CardHeader className="pb-2">
        <CardTitle className="text-base">Delivery History</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3 text-sm">
        <div className="flex flex-wrap items-end gap-2">
          <div className="space-y-1">
            <Label htmlFor="webhook-retention-days">Keep Delivery Logs for (Days)</Label>
            <Input
              id="webhook-retention-days"
              inputMode="numeric"
              className="h-9 w-28"
              value={value}
              onChange={(e) => setDraft(e.target.value)}
              aria-invalid={parsed === null}
              data-testid="webhook-retention-input"
            />
          </div>
          <Button variant="outline"
            type="button"
            size="sm"
            disabled={!dirty || save.isPending}
            onClick={() => parsed !== null && save.mutate(parsed)}
            data-testid="webhook-retention-save"
          >
            {save.isPending && <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />}
            Save
          </Button>
        </div>
        {parsed === null && (
          <p className="text-xs text-destructive">
            Enter whole days between {s.min_retention_days} and {s.max_retention_days}.
          </p>
        )}
        <p className="text-xs text-muted-foreground">
          Older delivery-log rows are deleted every hour (default{" "}
          {s.default_retention_days} days). Failed deliveries kept for
          redelivery are dropped after {s.dead_letter_retention_days} days.
          A webhook that fails {s.auto_disable_after_failures} deliveries in a
          row for at least {s.auto_disable_min_streak_hours} hours is turned
          off automatically, and HR admins are notified.
        </p>
      </CardContent>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// CreateWebhookDialog
// ---------------------------------------------------------------------------

function CreateWebhookDialog() {
  const [open, setOpen] = useState(false);
  const [revealed, setRevealed] = useState<{
    secret: string;
    label: string;
  } | null>(null);
  const createMut = useCreateWebhook();

  const form = useForm<CreateFormValues>({
    resolver: zodResolver(createSchema),
    defaultValues: {
      provider: "slack",
      url: "",
      label: "",
      category: "*",
      all_events: true,
      event_types: [],
      is_active: true,
    },
  });

  const pending = createMut.isPending;
  const provider = form.watch("provider");
  const isGeneric = provider === "generic";

  async function onSubmit(values: CreateFormValues) {
    const generic = values.provider === "generic";
    try {
      const created = await createMut.mutateAsync({
        provider: values.provider,
        url: values.url.trim(),
        label: values.label.trim(),
        ...(generic
          ? { event_types: values.all_events ? [] : values.event_types }
          : { category: values.category }),
        is_active: values.is_active,
      });
      toast.success(`Webhook "${values.label.trim()}" created.`);
      setOpen(false);
      form.reset();
      if (created.signing_secret) {
        setRevealed({
          secret: created.signing_secret,
          label: values.label.trim(),
        });
      }
    } catch (err) {
      const e = toApiError(err);
      if (e.field && e.field in form.getValues()) {
        form.setError(e.field as keyof CreateFormValues, {
          message: e.message,
        });
      } else {
        toast.error(e.message);
      }
    }
  }

  return (
    <>
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button className="gap-1.5" data-testid="webhook-add-btn">
          <Plus className="h-4 w-4" />
          Add Webhook
        </Button>
      </DialogTrigger>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-md">
        <DialogHeader>
          <DialogTitle>New Outbound Webhook</DialogTitle>
          <DialogDescription>
            WorkRight will POST to this URL when the selected events happen.
            Use a Slack incoming webhook, an MS Teams connector URL, or your
            own HTTPS endpoint (signed JSON events).
          </DialogDescription>
        </DialogHeader>
        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(onSubmit)}
            className="space-y-4"
            noValidate
          >
            <FormField
              control={form.control}
              name="provider"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Provider *</FormLabel>
                  <Select value={field.value} onValueChange={field.onChange}>
                    <FormControl>
                      <SelectTrigger data-testid="webhook-provider-select">
                        <SelectValue placeholder="Select provider" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <SelectItem value="slack">Slack</SelectItem>
                      <SelectItem value="teams">Microsoft Teams</SelectItem>
                      <SelectItem value="generic">
                        Your HTTPS Endpoint (Signed)
                      </SelectItem>
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="url"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Webhook URL *</FormLabel>
                  <FormControl>
                    <Input
                      placeholder={
                        isGeneric
                          ? "https://erp.example.com/webhooks/workright"
                          : "https://hooks.slack.com/services/..."
                      }
                      autoComplete="off"
                      data-testid="webhook-url-input"
                      {...field}
                    />
                  </FormControl>
                  <FormDescription>
                    {isGeneric ? (
                      <>
                        A public HTTPS address. WorkRight never connects to
                        private, loopback or cloud-metadata addresses and
                        does not follow redirects. Only the masked URL is
                        shown after creation.
                      </>
                    ) : (
                      <>
                        Slack: copy from &quot;Incoming Webhooks&quot; app
                        integration. Teams: use the &quot;Incoming
                        Webhook&quot; connector URL. Only the masked URL is
                        shown after creation.
                      </>
                    )}
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="label"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Label *</FormLabel>
                  <FormControl>
                    <Input
                      placeholder="e.g. #hr-alerts"
                      data-testid="webhook-label-input"
                      {...field}
                    />
                  </FormControl>
                  <FormDescription>
                    Shown in the webhook list and in test-fire messages.
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />
            {isGeneric ? (
              <GenericSubscriptionField
                allEvents={form.watch("all_events")}
                eventTypes={form.watch("event_types")}
                error={form.formState.errors.event_types?.message}
                onAllEventsChange={(v) =>
                  form.setValue("all_events", v, { shouldValidate: true })
                }
                onEventTypesChange={(v) =>
                  form.setValue("event_types", v, { shouldValidate: true })
                }
              />
            ) : (
              <FormField
                control={form.control}
                name="category"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Notification Type *</FormLabel>
                    <CategorySelect
                      value={field.value}
                      onChange={field.onChange}
                      testId="webhook-category-select"
                    />
                    <FormDescription>
                      &quot;All notifications&quot; fires for every in-app
                      notice except confidential POSH notices and
                      personal-finance notices (employee loans and tax
                      declarations). Data events such as invoices, bills
                      and payroll locks are sent only to a channel that
                      picks them (or their &quot;All … events&quot; family)
                      here.
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
            )}
            <FormField
              control={form.control}
              name="is_active"
              render={({ field }) => (
                <FormItem>
                  <label className="flex items-start gap-3 text-sm">
                    <FormControl>
                      <input
                        type="checkbox"
                        className="mt-0.5 h-4 w-4 rounded border-input accent-primary"
                        checked={field.value}
                        onChange={(e) => field.onChange(e.target.checked)}
                        onBlur={field.onBlur}
                        ref={field.ref}
                        name={field.name}
                        data-testid="webhook-active-checkbox"
                      />
                    </FormControl>
                    <span>
                      <span className="font-medium">Active</span>
                      <span className="block text-muted-foreground">
                        Inactive webhooks are skipped during dispatch.
                      </span>
                    </span>
                  </label>
                </FormItem>
              )}
            />
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => setOpen(false)}
                disabled={pending}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={pending}
                className="gap-1.5"
                data-testid="webhook-submit-btn"
              >
                {pending && <Loader2 className="h-4 w-4 animate-spin" />}
                Create
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
    {revealed && (
      <SigningSecretDialog
        secret={revealed.secret}
        title={`Webhook Created: ${revealed.label}`}
        onClose={() => setRevealed(null)}
      />
    )}
    </>
  );
}

// ---------------------------------------------------------------------------
// GenericSubscriptionField — event_types picker for provider "generic"
// ---------------------------------------------------------------------------

function GenericSubscriptionField({
  allEvents,
  eventTypes,
  error,
  onAllEventsChange,
  onEventTypesChange,
}: {
  readonly allEvents: boolean;
  readonly eventTypes: string[];
  readonly error?: string;
  readonly onAllEventsChange: (v: boolean) => void;
  readonly onEventTypesChange: (v: string[]) => void;
}) {
  return (
    <div className="space-y-1.5" data-testid="webhook-generic-subscription">
      <Label>Events *</Label>
      <EventTypePicker
        allEvents={allEvents}
        onAllEventsChange={onAllEventsChange}
        value={eventTypes}
        onChange={onEventTypesChange}
      />
      {error && (
        <p className="text-sm font-medium text-destructive">{error}</p>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// EditWebhookDialog
// ---------------------------------------------------------------------------

function editDefaults(webhook: OutboundWebhook): EditFormValues {
  return {
    provider: webhook.provider,
    label: webhook.label,
    category: webhook.category,
    all_events: webhook.event_types.length === 0,
    event_types: webhook.event_types,
    is_active: webhook.is_active,
  };
}

function EditWebhookDialog({ webhook }: { readonly webhook: OutboundWebhook }) {
  const [open, setOpen] = useState(false);
  const updateMut = useUpdateWebhook();
  const isGeneric = webhook.provider === "generic";

  const form = useForm<EditFormValues>({
    resolver: zodResolver(editSchema),
    defaultValues: editDefaults(webhook),
  });

  function handleOpenChange(v: boolean) {
    setOpen(v);
    if (v) {
      form.reset(editDefaults(webhook));
    }
  }

  const pending = updateMut.isPending;

  async function onSubmit(values: EditFormValues) {
    try {
      await updateMut.mutateAsync({
        id: webhook.id,
        body: {
          label: values.label.trim(),
          ...(isGeneric
            ? { event_types: values.all_events ? [] : values.event_types }
            : { category: values.category }),
          is_active: values.is_active,
        },
      });
      toast.success(`Webhook "${values.label.trim()}" updated.`);
      setOpen(false);
    } catch (err) {
      const e = toApiError(err);
      if (e.field && e.field in form.getValues()) {
        form.setError(e.field as keyof EditFormValues, { message: e.message });
      } else {
        toast.error(e.message);
      }
    }
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>
        <Button
          size="sm"
          variant="outline"
          className="gap-1.5"
          data-testid={`webhook-edit-btn-${webhook.id}`}
        >
          <Pencil className="h-3.5 w-3.5" />
          Edit
        </Button>
      </DialogTrigger>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Edit Webhook</DialogTitle>
          <DialogDescription>
            Update the label, {isGeneric ? "events" : "notification type"}, or
            active state. To change the URL, delete this webhook and create a
            new one.
          </DialogDescription>
        </DialogHeader>
        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(onSubmit)}
            className="space-y-4"
            noValidate
          >
            <div className="rounded-md bg-muted px-3 py-2 text-sm text-muted-foreground">
              <span className="font-medium text-foreground">URL: </span>
              {webhook.url_masked}
            </div>
            <FormField
              control={form.control}
              name="label"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Label *</FormLabel>
                  <FormControl>
                    <Input {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            {isGeneric ? (
              <GenericSubscriptionField
                allEvents={form.watch("all_events")}
                eventTypes={form.watch("event_types")}
                error={form.formState.errors.event_types?.message}
                onAllEventsChange={(v) =>
                  form.setValue("all_events", v, { shouldValidate: true })
                }
                onEventTypesChange={(v) =>
                  form.setValue("event_types", v, { shouldValidate: true })
                }
              />
            ) : (
              <FormField
                control={form.control}
                name="category"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Notification Type *</FormLabel>
                    <CategorySelect
                      value={field.value}
                      onChange={field.onChange}
                    />
                    <FormMessage />
                  </FormItem>
                )}
              />
            )}
            <FormField
              control={form.control}
              name="is_active"
              render={({ field }) => (
                <FormItem>
                  <label className="flex items-start gap-3 text-sm">
                    <FormControl>
                      <input
                        type="checkbox"
                        className="mt-0.5 h-4 w-4 rounded border-input accent-primary"
                        checked={field.value}
                        onChange={(e) => field.onChange(e.target.checked)}
                        onBlur={field.onBlur}
                        ref={field.ref}
                        name={field.name}
                      />
                    </FormControl>
                    <span>
                      <span className="font-medium">Active</span>
                      <span className="block text-muted-foreground">
                        Inactive webhooks are skipped during dispatch.
                      </span>
                    </span>
                  </label>
                </FormItem>
              )}
            />
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => setOpen(false)}
                disabled={pending}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={pending} className="gap-1.5">
                {pending && <Loader2 className="h-4 w-4 animate-spin" />}
                Save Changes
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// WebhookRow — single webhook card
// ---------------------------------------------------------------------------

function WebhookRow({
  webhook,
  isHrAdmin,
}: {
  readonly webhook: OutboundWebhook;
  readonly isHrAdmin: boolean;
}) {
  const [showLog, setShowLog] = useState(false);
  const deleteMut = useDeleteWebhook();
  const updateMut = useUpdateWebhook();
  const testMut = useTestWebhook();

  const busy =
    deleteMut.isPending || updateMut.isPending || testMut.isPending;

  async function handleToggleActive() {
    try {
      await updateMut.mutateAsync({
        id: webhook.id,
        body: { is_active: !webhook.is_active },
      });
      toast.success(
        webhook.is_active
          ? `Webhook "${webhook.label}" disabled.`
          : `Webhook "${webhook.label}" enabled.`,
      );
    } catch (err) {
      toast.error(toApiError(err).message);
    }
  }

  async function handleDelete() {
    try {
      await deleteMut.mutateAsync(webhook.id);
      toast.success(`Webhook "${webhook.label}" deleted.`);
    } catch (err) {
      toast.error(toApiError(err).message);
    }
  }

  async function handleTest() {
    try {
      const res = await testMut.mutateAsync(webhook.id);
      if (res.status_code >= 200 && res.status_code < 300) {
        toast.success(
          `Test fired — ${webhook.label} responded ${res.status_code}.`,
        );
      } else {
        // The endpoint's reply is cut to one plain line — an HTML error
        // page must not flood the toast (QA books #13).
        const reply = webhookErrorText(res.error);
        const detail = reply ? `: ${reply}` : "";
        toast.error(`Test failed (${res.status_code || "no response"})${detail}`);
      }
    } catch (err) {
      toast.error(toApiError(err).message);
    }
  }

  const lastStatusTone = deliveryStatusTone(webhook.last_status);

  return (
    <Card data-testid={`webhook-row-${webhook.id}`}>
      <CardHeader className="pb-2">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0 flex-1">
            <CardTitle className="flex flex-wrap items-center gap-2 text-base">
              <Send className="h-4 w-4 shrink-0 text-primary" />
              {webhook.label}
            </CardTitle>
            <div className="mt-2 flex flex-wrap items-center gap-2 text-sm">
              <StatusBadge
                tone="info"
                label={providerLabel(webhook.provider)}
              />
              <span className="text-muted-foreground">
                {categoryLabel(webhook)}
              </span>
              <span className="font-mono text-xs text-muted-foreground">
                {webhook.url_masked}
              </span>
              {webhook.is_active ? (
                <StatusBadge tone="success" label="Active" />
              ) : (
                <StatusBadge tone="neutral" label="Inactive" />
              )}
              {webhook.last_status != null && (
                <StatusBadge
                  tone={lastStatusTone}
                  label={`Last: ${webhook.last_status}`}
                />
              )}
              {webhook.is_active && (webhook.consecutive_failures ?? 0) > 0 && (
                <StatusBadge
                  tone="danger"
                  label={`${webhook.consecutive_failures} Failed in a Row`}
                />
              )}
            </div>
          </div>
          {isHrAdmin && (
            <div className="flex shrink-0 flex-wrap items-center gap-2">
              <Button
                size="sm"
                variant="outline"
                className="gap-1.5"
                onClick={() => void handleTest()}
                disabled={busy || !webhook.is_active}
                title={
                  webhook.is_active
                    ? "Send a test event"
                    : "Enable the webhook to test"
                }
                data-testid={`webhook-test-btn-${webhook.id}`}
              >
                {testMut.isPending ? (
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                ) : (
                  <FlaskConical className="h-3.5 w-3.5" />
                )}
                Test
              </Button>
              <EditWebhookDialog webhook={webhook} />
              {webhook.provider === "generic" && (
                <RotateSecretButton webhook={webhook} disabled={busy} />
              )}
              <Button
                size="sm"
                variant="outline"
                className="gap-1.5"
                onClick={() => void handleToggleActive()}
                disabled={busy}
                data-testid={`webhook-toggle-btn-${webhook.id}`}
              >
                {updateMut.isPending && (
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                )}
                {!updateMut.isPending && webhook.is_active && (
                  <AlertTriangle className="h-3.5 w-3.5" />
                )}
                {!updateMut.isPending && !webhook.is_active && (
                  <CheckCircle2 className="h-3.5 w-3.5" />
                )}
                {webhook.is_active ? "Disable" : "Enable"}
              </Button>
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button
                    size="sm"
                    variant="destructive"
                    className="gap-1.5"
                    disabled={busy}
                    data-testid={`webhook-delete-btn-${webhook.id}`}
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                    Delete
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>Delete webhook?</AlertDialogTitle>
                    <AlertDialogDescription>
                      &quot;{webhook.label}&quot; ({webhook.url_masked}) will
                      be permanently deleted along with its delivery history.
                      This cannot be undone.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Cancel</AlertDialogCancel>
                    <AlertDialogAction
                      onClick={() => void handleDelete()}
                      className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                    >
                      {deleteMut.isPending ? (
                        <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />
                      ) : null}
                      Delete
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </div>
          )}
        </div>
      </CardHeader>
      <CardContent className="pb-0">
        <AutoDisabledBanner webhook={webhook} isHrAdmin={isHrAdmin} />
        <button
          type="button"
          className="flex w-full items-center gap-1.5 py-2 text-xs text-muted-foreground hover:text-foreground"
          onClick={() => setShowLog((v) => !v)}
          data-testid={`webhook-log-toggle-${webhook.id}`}
        >
          {showLog ? (
            <ChevronUp className="h-3.5 w-3.5" />
          ) : (
            <ChevronDown className="h-3.5 w-3.5" />
          )}
          {showLog ? "Hide" : "Show"} delivery log
        </button>
        {showLog && (
          <DeliveryLog
            webhookId={webhook.id}
            canRedeliver={isHrAdmin && webhook.provider === "generic" && webhook.is_active}
          />
        )}
        {webhook.provider === "generic" && <DeadLetterPanel webhook={webhook} />}
      </CardContent>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Page component
// ---------------------------------------------------------------------------

function SettingsWebhooksRoute() {
  const { isHrAdmin } = useHrRoles();
  const q = useWebhooks();
  const rows = q.data?.items ?? [];

  return (
    <>
      <SettingsPageHeader />
      <SettingsShell groups={SETTINGS_GROUPS}>
        <div className="space-y-6">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <h2 className="text-lg font-semibold">Outbound Webhooks</h2>
              <p className="text-sm text-muted-foreground">
                Push WorkRight notifications to Slack or Microsoft Teams, or
                send signed JSON events to your own HTTPS endpoint (failed
                deliveries are retried for up to 8 hours). Webhook URLs are
                only shown masked after creation.
              </p>
            </div>
            {isHrAdmin && <CreateWebhookDialog />}
          </div>

          {!isHrAdmin && (
            <Alert>
              <AlertTitle>Read-only</AlertTitle>
              <AlertDescription>
                Only HR managers and company admins can manage webhook
                subscriptions.
              </AlertDescription>
            </Alert>
          )}

          {q.isLoading && (
            <div className="space-y-3">
              {["w1", "w2", "w3"].map((k) => (
                <Skeleton key={k} className="h-24 w-full" />
              ))}
            </div>
          )}

          {q.isError && (
            <Alert variant="destructive">
              <AlertTitle>Couldn&apos;t load webhooks</AlertTitle>
              <AlertDescription>
                {toApiError(q.error).message}
              </AlertDescription>
            </Alert>
          )}

          {q.data && rows.length === 0 && (
            <Card className="border-dashed">
              <CardContent className="flex flex-col items-center gap-3 py-12 text-center">
                <span className="flex h-12 w-12 items-center justify-center rounded-lg bg-muted">
                  <Webhook className="h-6 w-6 text-muted-foreground" />
                </span>
                <div className="space-y-1">
                  <p className="text-base font-medium">No webhooks yet</p>
                  <p className="max-w-sm text-sm text-muted-foreground">
                    {isHrAdmin
                      ? "Add a webhook above to start receiving WorkRight events in Slack, Teams or your own system."
                      : "Ask an HR admin to configure outbound webhooks."}
                  </p>
                </div>
              </CardContent>
            </Card>
          )}

          {q.data && rows.length > 0 && (
            <div className="space-y-3" data-testid="webhooks-list">
              {rows.map((w) => (
                <WebhookRow key={w.id} webhook={w} isHrAdmin={isHrAdmin} />
              ))}
            </div>
          )}

          <DeliveryRetentionCard isHrAdmin={isHrAdmin} />
        </div>
      </SettingsShell>
    </>
  );
}
